import styled, { css } from "styled-components";

const appleEase = "0.22s cubic-bezier(0.25, 0.1, 0.25, 1)";

export const listingImageHeight = 168;

const pillBase = css`
    font-family: ${({ theme }) => theme.fontFamily.body};
    font-size: 11px;
    font-weight: 500;
    letter-spacing: -0.01em;
    line-height: 1.2;
    text-align: center;
    border-radius: 100px;
    padding: 5px 10px;
    pointer-events: none;
    white-space: nowrap;
`;

export const listingCarouselStyles = css`
    .react-multiple-carousel__arrow {
        z-index: 10;
        min-width: 28px;
        min-height: 28px;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.94);
        box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
        opacity: 0;
        transition: opacity ${appleEase};
    }

    .react-multiple-carousel__arrow::before {
        color: #1d1d1f;
        font-size: 11px;
        font-weight: 700;
    }

    .react-multiple-carousel__arrow--left {
        left: 10px;
    }

    .react-multiple-carousel__arrow--right {
        right: 10px;
    }

    &:hover .react-multiple-carousel__arrow {
        opacity: 1;
    }

    .react-multi-carousel-dot-list {
        bottom: 10px;
        gap: 3px;
    }

    .react-multi-carousel-dot button {
        border: none;
        width: 6px;
        height: 6px;
        background: rgba(255, 255, 255, 0.5);
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.04);
    }

    .react-multi-carousel-dot--active button {
        background: #ffffff;
    }
`;

export const ListingCardShell = styled.div<{ $compact?: boolean; $static?: boolean }>`
    background: #fff;
    min-width: ${({ $compact }) => ($compact ? "240px" : "260px")};
    max-width: 360px;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 18px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: none;
    transition: transform ${appleEase}, box-shadow ${appleEase}, border-color ${appleEase};
    ${listingCarouselStyles}

    ${({ $static }) =>
        $static
            ? css`
                  border: none;
                  border-radius: 0;
                  max-width: none;

                  &:hover {
                      transform: none;
                      box-shadow: none;
                      border-color: transparent;
                  }
              `
            : css`
                  &:hover {
                      transform: translateY(-3px);
                      border-color: rgba(0, 0, 0, 0.08);
                      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.03);
                  }
              `}
`;

export const ListingImageFrame = styled.div<{ $height?: number }>`
    position: relative;
    width: 100%;
    height: ${({ $height }) => $height ?? listingImageHeight}px;
    overflow: hidden;
    background: #f5f5f7;

    &::after {
        content: "";
        position: absolute;
        inset: 0 0 auto;
        height: 64px;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.28) 0%, transparent 100%);
        pointer-events: none;
        z-index: 1;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.55s cubic-bezier(0.25, 0.1, 0.25, 1);
    }

    ${ListingCardShell}:hover & img {
        transform: scale(1.03);
    }

    .react-multiple-carousel__arrow {
        z-index: 3;
    }
`;

export const HeartContainer = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 5;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: saturate(180%) blur(16px);
    -webkit-backdrop-filter: saturate(180%) blur(16px);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
`;

export const TagContainer = styled.div`
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 4;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    max-width: calc(100% - 56px);
    pointer-events: none;
`;

export const OverlayPill = styled.div`
    ${pillBase}
    background: rgba(255, 255, 255, 0.92);
    color: #1d1d1f;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    backdrop-filter: saturate(180%) blur(16px);
    -webkit-backdrop-filter: saturate(180%) blur(16px);
`;

export const Exclusive = styled.div`
    ${pillBase}
    background: ${({ theme }) => theme.color.manorleadGold};
    color: #1d1d1f;
`;

export const Hot = styled.div`
    ${pillBase}
    background: #ff7755;
    color: ${({ theme }) => theme.color.white};
`;

export const OpenHouse = styled.div`
    ${pillBase}
    background: #1d1d1f;
    color: ${({ theme }) => theme.color.white};
`;

export const Tour = styled.div`
    ${pillBase}
    background: rgba(92, 74, 169, 0.94);
    color: ${({ theme }) => theme.color.white};
`;

export const ListingBody = styled.div`
    display: flex;
    flex-direction: column;
    padding: 12px 14px 14px;
    white-space: nowrap;
    cursor: pointer;
    min-height: 0;
`;

export const ListingPrice = styled.div<{ $sold?: boolean }>`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-weight: 600;
    letter-spacing: -0.022em;
    line-height: 1.15;
    color: ${({ $sold, theme }) => ($sold ? theme.color.manorleadGold : "#1d1d1f")};
    font-size: 18px;
    display: flex;
    align-items: center;
    gap: 8px;

    @media (max-width: 575px) {
        font-size: 17px;
    }
`;

export const ListingAddress = styled.div`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-weight: 400;
    letter-spacing: -0.01em;
    line-height: 1.3;
    color: #6e6e73;
    font-size: 13px;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const ListingSpecs = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 4px 12px;
    margin-top: 8px;
    overflow: hidden;
`;

export const ListingSpec = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: #1d1d1f;

    svg {
        width: 15px;
        height: 15px;
        flex-shrink: 0;
    }
`;

export const ListingSpecLabel = styled.span`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-weight: 400;
    font-size: 12px;
    letter-spacing: -0.01em;
    color: #1d1d1f;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const ListingMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    min-width: 0;
`;

export const GoldDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.color.manorleadGold};
    flex-shrink: 0;
`;

export const ListingBrokerage = styled.div`
    font-family: ${({ theme }) => theme.fontFamily.homeBody};
    font-size: 12px;
    letter-spacing: -0.01em;
    color: #86868b;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const StatusTag = styled.div<{ $muted?: boolean }>`
    ${pillBase}
    display: inline-flex;
    align-self: flex-start;
    margin-bottom: 10px;
    pointer-events: none;
    background: ${({ $muted, theme }) =>
        $muted ? theme.color.blackPrimaryL : theme.color.manorleadGold};
    color: ${({ $muted, theme }) => ($muted ? theme.color.white : "#1d1d1f")};
`;
